'use client';

/**
 * Deferred activation for the Taboola loader.
 *
 * WHY: PSI field data (CrUX, 28 days, real users) for dinamani.com/article
 * failed Core Web Vitals on **LCP 3.4s** — the only failing metric (INP 200ms
 * and CLS 0.02 both pass). Real users are not covered by the Lighthouse UA gate
 * in `isLighthouse.ts`, so they load the FULL third-party stack on every
 * article: Taboola alone was ~40 requests across 4 hosts plus a meaningful
 * slice of main-thread time, and it ran eagerly on mount regardless of whether
 * its widgets were anywhere near the viewport.
 *
 * `_taboola` is a plain queue array until the library replaces it, so every
 * `push()` in TaboolaWidget / TaboolaRightRail / TaboolaInfiniteScrollWidgets
 * stays eager and costs nothing — ALL the careful ordering (page-type marker,
 * `newPageLoad` notify, debounced `flush`) is preserved exactly as-is. The only
 * thing deferred is the one expensive step: injecting `loader.js`.
 *
 * Widgets call `requestTaboolaLoad()` when their container scrolls near the
 * viewport; `TaboolaLoader` registers the injection itself. Whichever happens
 * second triggers the load, so mount order between them doesn't matter.
 */

type Injector = () => void;

let injector: Injector | null = null;
let requested = false;
let injected = false;

function runIfReady(): void {
  if (injected || !requested || !injector) return;
  injected = true;
  injector();
}

/**
 * Called by TaboolaLoader. Stores how to inject loader.js; fires immediately if
 * a widget already asked for it (widget mounted before the loader).
 */
export function registerTaboolaInjector(fn: Injector): void {
  injector = fn;
  runIfReady();
}

/**
 * Called by a widget once its container is near the viewport (or on first user
 * interaction). Idempotent — the first call wins, later ones are no-ops.
 */
export function requestTaboolaLoad(): void {
  if (requested) return;
  requested = true;
  runIfReady();
}

/** Has loader.js already been injected this session? */
export function isTaboolaLoaded(): boolean {
  return injected;
}

/**
 * Observe a widget container and request the Taboola load when it comes within
 * `rootMargin`. Returns a cleanup function.
 *
 * Also arms a one-shot first-interaction fallback: a reader who interacts with
 * the page is likely to reach the widgets, and this guarantees Taboola still
 * loads on browsers without IntersectionObserver or in layouts where the
 * container never intersects.
 */
export function observeForTaboolaLoad(el: HTMLElement | null): () => void {
  if (typeof window === 'undefined') return () => {};
  if (injected || requested) return () => {};

  const cleanups: Array<() => void> = [];

  if (el && typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) requestTaboolaLoad();
      },
      // Generous margin: start the fetch before the widget is actually on
      // screen so it isn't visibly empty when the reader arrives.
      { rootMargin: '800px 0px' },
    );
    io.observe(el);
    cleanups.push(() => io.disconnect());
  } else {
    // No observer available — don't silently drop the widget.
    requestTaboolaLoad();
    return () => {};
  }

  const onInteract = () => requestTaboolaLoad();
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
  for (const ev of events) {
    window.addEventListener(ev, onInteract, { once: true, passive: true });
    cleanups.push(() => window.removeEventListener(ev, onInteract));
  }

  return () => cleanups.forEach((fn) => fn());
}
